import { Navigation } from 'swiper/modules';
import Swiper from 'swiper';

interface SendcloudServicePoint {
  id: number;
  code: string;
  name: string;
  carrier: string; // e.g., 'postnl', 'dhl'
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string; // ISO 2-letter code, e.g., 'NL'
  latitude: string;
  longitude: string;
  distance: number; // Distance in meters from reference point
  email: string;
  phone: string;
  homepage: string;
  shop_type?: string | null; // e.g., 'service_point', 'locker'
  is_active: boolean; // Indicates if data was recently updated/scraped
  open_tomorrow: boolean;
  open_upcoming_week: boolean;
  formatted_opening_times: FormattedOpeningTimes;
  extra_data: Record<string, any>; // Carrier-specific data
}

interface FormattedOpeningTimes {
  monday: string[];
  tuesday: string[];
  wednesday: string[];
  thursday: string[];
  friday: string[];
  saturday: string[];
  sunday: string[];
}

export default function initShipping() {
  Alpine.data('shipping', (
    selectedShippingType: string,
    selectedShippingMethod: string,
  ) => ({
    type: selectedShippingType,
    method: selectedShippingMethod,
    servicePointRequestId: 0,
    updateShippingMethodReady() {
      if (this.type !== 'pickupPoint') {
        Alpine.store('shippingMethodReady', true);
        Alpine.store('shippingMethodSaving', false);
        return;
      }

      const selectedMethod = document.querySelector(`.shipping-method input[value="${this.method}"]`)?.closest('.shipping-method') as HTMLElement | null;
      const pointId = selectedMethod?.querySelector('.point-id') as HTMLInputElement | null;
      Alpine.store('shippingMethodReady', !!pointId?.value);
    },
    init() {
      const swiperEl = this.$el.querySelector('.address-swiper') as HTMLElement;
      if (swiperEl) {
        // eslint-disable-next-line no-new
        new Swiper(swiperEl, {
          modules: [Navigation],
          spaceBetween: 16,
          slidesPerView: 'auto',
          navigation: {
            nextEl: `#${(swiperEl.parentNode as HTMLElement).id} .address-swiper-button-next`,
            prevEl: `#${(swiperEl.parentNode as HTMLElement).id} .address-swiper-button-prev`,
            disabledClass: 'hidden',
          },
        });
      }
      this.updateShippingMethodReady();

      this.$watch('type', () => {
        this.updateShippingMethodReady();
      });

      this.$watch('method', () => {
        this.updateShippingMethodReady();
      });
    },
    openAddressForm(event: Event) {
      event.stopPropagation();
      event.preventDefault();
      const { id } = (event.currentTarget as HTMLElement).dataset;
      const dialog = document.getElementById('address-dialog');
      if (dialog) {
        dialog.querySelectorAll('.address-form').forEach((addressForm) => {
          if (addressForm.getAttribute('id') === id) {
            addressForm.classList.remove('hidden');
          } else {
            addressForm.classList.add('hidden');
          }
        });
        // @ts-ignore
        if (!dialog.open) {
          // @ts-ignore
          dialog.show();
        }
      }
    },
    openDeleteModal(event: Event) {
      event.stopPropagation();
      event.preventDefault();
      const { id } = (event.currentTarget as HTMLElement).dataset;
      const dialog = document.getElementById(`delete-address-dialog-${id}`);
      if (dialog) {
        // @ts-ignore
        dialog.show();
      }
    },
    openServicePointPicker(event: Event, shippingHandle: string, cartNumber: string) {
      event.preventDefault();
      event.stopPropagation();
      if (event.currentTarget) {
        const config = (event.currentTarget as HTMLElement).dataset;
        sendcloud.servicePoints.open(
          config,
          async (servicePointObject: SendcloudServicePoint) => {
            this.servicePointRequestId += 1;
            const requestId = this.servicePointRequestId;
            const sppInfo = document.getElementById(`service-point-address-${shippingHandle}`) as HTMLElement;
            if (sppInfo) {
              sppInfo.classList.remove('hidden');
              (sppInfo.querySelector('.family-name') as HTMLElement).innerHTML = servicePointObject.name;
              (sppInfo.querySelector('.address-line1') as HTMLElement).innerHTML = `${servicePointObject.street} ${servicePointObject.house_number}`;
              (sppInfo.querySelector('.postal-code') as HTMLElement).innerHTML = servicePointObject.postal_code;
              (sppInfo.querySelector('.locality') as HTMLElement).innerHTML = servicePointObject.city;
              (sppInfo.querySelector('.point-id') as HTMLInputElement).value = `${servicePointObject.id}`;
              (sppInfo.querySelector('.address-card') as HTMLInputElement).classList.remove('border', 'border-gray-300', 'py-4.5');
              (sppInfo.querySelector('.address-card') as HTMLInputElement).classList.add('selected', 'py-6', 'border-2', 'border-brand');
              (sppInfo.querySelector('.address-card .address-card-data') as HTMLInputElement).classList.remove('hidden');
              (sppInfo.querySelector('.service-point-picker') as HTMLInputElement).classList.remove('text-price-sale', '[&>svg]:fill-price-sale', 'hover:decoration-price-sale');
              (sppInfo.querySelector('.service-point-picker') as HTMLInputElement).classList.add('text-brand', '[&>svg]:fill-brand', 'hover:decoration-brand');
            }
            Alpine.store('shippingMethodReady', false);
            Alpine.store('shippingMethodSaving', true);

            const shippingMethodBlock = document.querySelector('.shipping-method.selected') as HTMLElement;
            if (shippingMethodBlock) {
              shippingMethodBlock.classList.remove('border-price-sale!', 'has-error');
            }

            try {
              const response = await fetch(Craft.sendCloudServicePointUrl, {
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'same-origin',
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  // @ts-ignore
                  'X-CSRF-Token': window.CRAFT_CSRF_TOKEN,
                },
                body: JSON.stringify({
                  orderNumber: cartNumber,
                  servicePoint: servicePointObject,
                }),
              });

              if (!response.ok) {
                throw new Error(`Could not save service point, received status ${response.status}`);
              }

              if (requestId !== this.servicePointRequestId) {
                return;
              }

              Alpine.store('shippingMethodReady', true);
            } catch {
              if (requestId !== this.servicePointRequestId) {
                return;
              }

              Alpine.store('shippingMethodReady', false);
            } finally {
              if (requestId === this.servicePointRequestId) {
                Alpine.store('shippingMethodSaving', false);
              }
            }
          },
        );
      }
    },
  }));
}
